import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  Calendar,
  CheckSquare,
  Users,
  FolderKanban,
  ArrowRight,
  Sparkles,
  UserPlus,
  ListTodo,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar
} from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [dailyPlan, setDailyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingStaffCount, setPendingStaffCount] = useState(0);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first if needed
        await fetch(`${API}/seed-data`, { 
          method: "POST", 
          credentials: "include" 
        });

        const [statsRes, chartsRes, planRes, pendingRes, tasksRes] = await Promise.all([
          fetch(`${API}/dashboard/stats`, { credentials: "include" }),
          fetch(`${API}/dashboard/charts`, { credentials: "include" }),
          fetch(`${API}/dashboard/daily-plan`, { credentials: "include" }),
          fetch(`${API}/admin/staff-applications?status=pending`, { credentials: "include" }),
          fetch(`${API}/tasks`, { credentials: "include" })
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (chartsRes.ok) setCharts(await chartsRes.json());
        if (planRes.ok) setDailyPlan(await planRes.json());
        if (pendingRes.ok) {
          const data = await pendingRes.json();
          setPendingStaffCount(data?.applications?.length ?? data?.total ?? 0);
        }
        if (tasksRes.ok) {
          const data = await tasksRes.json();
          setTasks(data?.tasks ?? data ?? []);
        }
      } catch {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const COLORS = ['#D4AF37', '#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header - Luminous Style */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#FFFFFF', textShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#E5C558] mt-1">Here's what's happening with your empire today.</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-[#E5C558]">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      {/* Stats Grid - Luminous Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="card-hover group transition-all duration-300 hover:scale-[1.02]" 
          data-testid="stat-today-revenue"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 30px rgba(255, 200, 100, 0.08), 0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#E5C558]">Today's Revenue</p>
                <p className="text-2xl font-bold mt-1" style={{ color: '#FFD700', textShadow: '0 0 15px rgba(255, 215, 0, 0.3)' }}>{formatCurrency(stats?.today_revenue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center transition-all group-hover:scale-110" style={{ background: 'rgba(255, 215, 0, 0.2)', boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}>
                <DollarSign className="text-[#FFD700]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover group transition-all duration-300 hover:scale-[1.02]" 
          data-testid="stat-week-revenue"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 30px rgba(255, 200, 100, 0.08), 0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#E5C558]">Weekly Revenue</p>
                <p className="text-2xl font-bold text-[#22C55E] mt-1">{formatCurrency(stats?.week_revenue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center transition-all group-hover:scale-110">
                <TrendingUp className="text-[#22C55E]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover group transition-all duration-300 hover:scale-[1.02]" 
          data-testid="stat-month-revenue"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 30px rgba(255, 200, 100, 0.08), 0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#E5C558]">Monthly Revenue</p>
                <p className="text-2xl font-bold text-[#FFFFFF] mt-1">{formatCurrency(stats?.month_revenue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#3B82F6]/15 flex items-center justify-center transition-all group-hover:scale-110">
                <Sparkles className="text-[#3B82F6]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover group transition-all duration-300 hover:scale-[1.02]" 
          data-testid="stat-month-profit"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 30px rgba(255, 200, 100, 0.08), 0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#E5C558]">Net Profit (MTD)</p>
                <p className={`text-2xl font-bold mt-1 ${stats?.month_profit >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                  {formatCurrency(stats?.month_profit)}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all group-hover:scale-110 ${stats?.month_profit >= 0 ? 'bg-[#22C55E]/15' : 'bg-[#EF4444]/15'}`}>
                {stats?.month_profit >= 0 ? (
                  <TrendingUp className="text-[#22C55E]" size={24} />
                ) : (
                  <TrendingDown className="text-[#EF4444]" size={24} />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats - Cinematic Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card style={{ background: 'rgba(18, 18, 18, 0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(39, 39, 42, 0.5)' }}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <TrendingDown className="text-[#EF4444]" size={18} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Month Expenses</p>
                <p className="text-lg font-semibold text-[#FFFFFF]">{formatCurrency(stats?.month_expenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(18, 18, 18, 0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(39, 39, 42, 0.5)' }}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <AlertCircle className="text-[#F59E0B]" size={18} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Tax Reserve ({stats?.tax_percentage}%)</p>
                <p className="text-lg font-semibold text-[#FFFFFF]">{formatCurrency(stats?.tax_reserve)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(18, 18, 18, 0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(39, 39, 42, 0.5)' }}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <FolderKanban className="text-[#D4AF37]" size={18} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Active Projects</p>
                <p className="text-lg font-semibold text-[#FFFFFF]">{stats?.active_projects || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(18, 18, 18, 0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(39, 39, 42, 0.5)' }}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <CheckSquare className="text-[#EF4444]" size={18} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Tasks Due Today</p>
                <p className="text-lg font-semibold text-[#FFFFFF]">{stats?.tasks_due_today || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row - Cinematic Glass Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card 
          data-testid="revenue-chart"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(212, 175, 55, 0.15)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)'
          }}
        >
          <CardHeader>
            <CardTitle className="text-lg text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts?.revenue_chart || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212, 175, 55, 0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#C9C9C9', fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fill: '#C9C9C9', fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(18, 18, 18, 0.95)', 
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '8px',
                      backdropFilter: 'blur(8px)'
                    }}
                    formatter={(value) => [`$${value}`, 'Revenue']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#D4AF37" 
                    strokeWidth={2}
                    dot={{ fill: '#D4AF37', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Expense Breakdown */}
        <Card 
          data-testid="expense-chart"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(212, 175, 55, 0.15)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)'
          }}
        >
          <CardHeader>
            <CardTitle className="text-lg text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Expense Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {charts?.expense_pie?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.expense_pie}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="amount"
                      nameKey="category"
                    >
                      {charts.expense_pie.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(18, 18, 18, 0.95)', 
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        backdropFilter: 'blur(8px)'
                      }}
                      formatter={(value) => [`$${value}`, 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#C9C9C9]">No expense data yet</p>
              )}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
              {charts?.expense_pie?.slice(0, 6).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                  <span className="text-xs text-[#C9C9C9] capitalize">{item.category?.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Game Plan - Cinematic Style */}
      <Card 
        data-testid="daily-game-plan"
        style={{
          background: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212, 175, 55, 0.25)',
          boxShadow: '0 0 30px rgba(212, 175, 55, 0.1), 0 4px 30px rgba(0, 0, 0, 0.4)'
        }}
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#D4AF37', fontFamily: 'Montserrat, sans-serif' }}>
            <Sparkles size={20} />
            Today&apos;s Moves
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/tasks")}
            className="text-[#C9C9C9] hover:text-[#D4AF37]"
          >
            View All <ChevronRight size={16} />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Pending Staff Approvals */}
            {pendingStaffCount > 0 && (
              <button
                onClick={() => navigate("/admin/staff-approvals")}
                className="w-full flex items-center gap-3 p-4 rounded-lg transition-all cursor-pointer text-left group"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.6)';
                  e.currentTarget.style.background = 'rgba(245, 158, 11, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                  e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)';
                }}
                data-testid="pending-staff-link"
              >
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                  <UserPlus size={20} className="text-[#F59E0B]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#FFFFFF]">
                    {pendingStaffCount} Staff Signup{pendingStaffCount > 1 ? 's' : ''} Awaiting Approval
                  </p>
                  <p className="text-xs text-[#C9C9C9]">Click to review and approve</p>
                </div>
                <ChevronRight size={18} className="text-[#71717A] group-hover:text-[#F59E0B] transition-colors" />
              </button>
            )}

            {/* Tasks from dailyPlan or tasks list */}
            {(tasks || []).slice(0, 3).map((task, idx) => (
              <button
                key={task.id || idx}
                onClick={() => navigate(`/tasks/${task.id || ''}`)}
                className="w-full flex items-center gap-3 p-4 rounded-lg transition-all cursor-pointer text-left group"
                style={{
                  background: 'rgba(26, 26, 26, 0.6)',
                  border: '1px solid rgba(39, 39, 42, 0.5)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
                  e.currentTarget.style.background = 'rgba(26, 26, 26, 0.8)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(39, 39, 42, 0.5)';
                  e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)';
                }}
                data-testid={`task-${task.id || idx}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <ListTodo size={20} className="text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#FFFFFF]">{task.title || task}</p>
                  <p className="text-xs text-[#C9C9C9]">
                    {task.due_date ? `Due: ${new Date(task.due_date).toLocaleDateString()}` : 'No due date'}
                  </p>
                </div>
                <ChevronRight size={18} className="text-[#71717A] group-hover:text-[#D4AF37] transition-colors" />
              </button>
            ))}

            {/* Reminders from dailyPlan */}
            {(dailyPlan?.reminders || []).slice(0, 2).map((reminder, idx) => (
              <button
                key={idx}
                onClick={() => navigate("/tasks")}
                className="w-full flex items-center gap-3 p-4 rounded-lg transition-all cursor-pointer text-left group"
                style={{
                  background: 'rgba(26, 26, 26, 0.6)',
                  border: '1px solid rgba(39, 39, 42, 0.5)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
                  e.currentTarget.style.background = 'rgba(26, 26, 26, 0.8)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(39, 39, 42, 0.5)';
                  e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)';
                }}
              >
                <ArrowRight size={18} className="text-[#D4AF37] flex-shrink-0" />
                <p className="flex-1 text-sm text-[#FFFFFF]">{reminder}</p>
                <ChevronRight size={18} className="text-[#71717A] group-hover:text-[#D4AF37] transition-colors" />
              </button>
            ))}

            {/* Empty state */}
            {pendingStaffCount === 0 && (tasks || []).length === 0 && (dailyPlan?.reminders || []).length === 0 && (
              <div className="text-center py-8">
                <CheckSquare size={48} className="text-[#22C55E] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">All caught up! No urgent tasks today.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profit Chart */}
      <Card className="bg-[#121214] border-[#27272A]" data-testid="profit-chart">
        <CardHeader>
          <CardTitle className="text-lg text-[#FAFAFA]">Profit Trend (30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.profit_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#71717A', fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fill: '#71717A', fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1A1A1D', 
                    border: '1px solid #27272A',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="revenue" fill="#D4AF37" name="Revenue" />
                <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
                <Bar dataKey="profit" fill="#22C55E" name="Profit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
